import { useEffect, useRef, useState } from "react";

// routes that should not trigger the session timeout
const ExcludedRoutes = [
	"/",
	"/login",
	"/member/login",
	"/verify-email",
	"/verification",
	"/guest/login",
	"/guest/signup",
	"/two-factor",
	"/thank-you",
	"/two-factor-setup",
];
const useSessionTimeout = () => {
	const ACTIVITY_EVENTS = ["mousemove", "keydown", "scroll", "click"];
	const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
	const SESSION_CHECK = 10000; // 10 seconds
	const [isSessionTimeout, setIsSessionTimeout] = useState(false);
	const [enableSessionTimer, setEnableSessionTimer] = useState(false);
	const timerRef = useRef(null);
	const ACTIVITY_KEY = "lastActivity";

	const isExcludedRoute = () => {
		return ExcludedRoutes.some((route) => window.location.pathname === route);
	};

	// set last active point in storage
	const updateLastActivity = () => {
		// on an excluded path, so do nothing
		if (isExcludedRoute()) return;

		// update session activity
		localStorage.setItem(ACTIVITY_KEY, Date.now().toString());

		if (!timerRef.current) {
			setEnableSessionTimer(true);
		}
	};

	// stops the current session checks
	const stopSessionCheck = () => {
		setEnableSessionTimer(false);
	};

	useEffect(() => {
		// verifies if session has timed out
		const hasTimedOut = () => {
			const lastActivity = parseInt(localStorage.getItem(ACTIVITY_KEY)) || 0;

			// no activity value
			if (lastActivity === 0) return true;

			return Date.now() - lastActivity > SESSION_TIMEOUT;
		};

		// verifies current sessionTimeout state
		const verifySessionTime = () => {
			// on an excluded path or timer not started so do nothing
			if (isExcludedRoute() || !enableSessionTimer) return;

			if (hasTimedOut()) {
				setIsSessionTimeout(true);
				setEnableSessionTimer(false);
				return;
			}

			setIsSessionTimeout(false);

			timerRef.current = setTimeout(() => {
				verifySessionTime();
			}, SESSION_CHECK);
		};

		// clears up the session timeout
		// and removes the activity key from localStorage
		const clearSessionTimeout = () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
				localStorage.removeItem(ACTIVITY_KEY);
			}
		};

		if (enableSessionTimer) {
			verifySessionTime();
		}

		return () => {
			clearSessionTimeout();
		};
	}, [SESSION_TIMEOUT, enableSessionTimer]);

	useEffect(() => {
		ACTIVITY_EVENTS.forEach((event) =>
			window.addEventListener(event, updateLastActivity)
		);

		return () => {
			ACTIVITY_EVENTS.forEach((event) =>
				window.removeEventListener(event, updateLastActivity)
			);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { isSessionTimeout, stopSessionCheck, updateLastActivity };
};

export default useSessionTimeout;
